/**
 * planStatusesAdmin.service.ts — Admin > Plan Statuses (legacy
 * lib/admin/admin-plan-status-list.php): each live stage of the account, in
 * pipeline order, with the statuses a plan in that stage can carry, through
 * /plan-statuses. Writes need Admin Access; the read stays open.
 *
 *   GET    /plan-statuses                 live stages, Inquiry first, each with its
 *                                         statuses (live and deleted) in order
 *   POST   /plan-statuses                 { stageId, name } last in the stage
 *   PUT    /plan-statuses/{id}            { name }
 *   POST   /plan-statuses/reorder         { stageId, ids } every listed status is
 *                                         filed under the stage, ordered 1, 2, 3 …
 *   DELETE /plan-statuses/{id}            soft delete
 *   POST   /plan-statuses/{id}/restore    live again, last in its stage
 *
 * Plans are never touched: a plan keeps a status that moves or is deleted.
 * A name with an emoji is refused (NAME_UNSUPPORTED_CHARACTERS).
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export const PLAN_STATUS_NAME_MAX = 255;

export interface PlanStatusEntry {
  id: number;
  name: string;
  /** The custom stage id it is filed under. */
  stageId: number;
  order: number;
  /** false = deleted (restorable). */
  active: boolean;
}

export interface PlanStatusStage {
  /** The custom stage id. */
  id: number;
  name: string;
  statuses: PlanStatusEntry[];
}

export function listPlanStatusStages(): Promise<PlanStatusStage[]> {
  return apiClient
    .get<ApiResponse<{ stages?: Partial<PlanStatusStage>[] } | null>>('/plan-statuses')
    .then((r) =>
      (r.data.data?.stages ?? []).map((s) => ({ id: Number(s.id) || 0, name: s.name ?? '', statuses: s.statuses ?? [] })),
    );
}

export function createPlanStatus(stageId: number, name: string): Promise<PlanStatusEntry> {
  return apiClient
    .post<ApiResponse<PlanStatusEntry>>('/plan-statuses', { stageId, name: name.trim() })
    .then((r) => r.data.data);
}

export function renamePlanStatus(id: number, name: string): Promise<PlanStatusEntry> {
  return apiClient.put<ApiResponse<PlanStatusEntry>>(`/plan-statuses/${id}`, { name: name.trim() }).then((r) => r.data.data);
}

/** One stage's statuses in their new order; a status dragged from another stage moves here. */
export function reorderPlanStatuses(stageId: number, ids: number[]): Promise<void> {
  return apiClient.post('/plan-statuses/reorder', { stageId, ids }).then(() => undefined);
}

/** Soft delete: the status leaves the pickers; plans that carry it keep it. */
export function deletePlanStatus(id: number): Promise<PlanStatusEntry> {
  return apiClient.delete<ApiResponse<PlanStatusEntry>>(`/plan-statuses/${id}`).then((r) => r.data.data);
}

export function restorePlanStatus(id: number): Promise<PlanStatusEntry> {
  return apiClient.post<ApiResponse<PlanStatusEntry>>(`/plan-statuses/${id}/restore`).then((r) => r.data.data);
}
